import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import { phoneUi } from "../ui/phoneUi";
import { USER_ROLES, filterUsers, loadUsers, markUserInactive, saveUser, userForm } from "../../features/users/userData";
import { deviceStatusText, devicesForUser, loadAdminDevices, signOutAdminDevice } from "../../features/users/adminDevices";
import { phoneConfirm } from "../ui/dialogs";
import "../mobile.css";

// The phone User Management page (plan Phase 2): the users (active, or every one); one user (?id=N);
// add or edit a user (name, role, wage, a new PIN typed by the person setting it, active) and mark one
// inactive; an Admin's remembered phones with Sign out. Time reports, charts, timesheets and camera clips
// stay on the desktop. Layout only:
// features/users/userData.js is the desktop page's own code.

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function userIdOf(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

export default function UsersScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listState, setListState] = useState({ searchTerm: "", showInactive: false });
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {user} (null user = a new one) | null
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null); // Mark inactive's refusal (the list's read has its own)
  const navigate = useNavigate();
  const fromList = useRef(false);
  const id = userIdOf(searchParams);

  const reread = useCallback(async () => {
    try {
      setUsers(await loadUsers());
      setError(null);
    } catch (err) {
      setError(err.message || "Couldn't reach JobQ.");
      setUsers((u) => u || []);
    }
  }, []);
  useEffect(() => { reread(); }, [reread]);

  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else setSearchParams({}, { replace: true });
  };
  const user = id && users ? users.find((u) => u.user_id === id) : null;

  // Busy from the question until the re-read is in, so a second tap can't ask again.
  const deactivate = async () => {
    setActionError(null);
    setBusy(true);
    try {
      const r = await markUserInactive({ user, ui: phoneUi });
      if (r.error) setActionError(r.error);
      if (r.done) await reread();
    } finally {
      setBusy(false);
    }
  };
  const closePanel = useCallback(() => setEditing(null), []);

  return (
    <div className="jq-screen" data-testid={id ? "phone-user" : "phone-users"}>
      {id ? (
        <UserDetail user={user} loading={users === null} error={actionError || (user ? null : error)} busy={busy} onAll={() => { setActionError(null); toList(); }} onEdit={() => setEditing({ user })} onDeactivate={deactivate} />
      ) : (
        <UserList
          users={users}
          error={error}
          state={listState}
          setState={setListState}
          onOpen={(uid) => { fromList.current = true; setSearchParams({ id: String(uid) }); }}
          onAdd={() => setEditing({ user: null })}
        />
      )}
      <UserPanel
        open={!!editing}
        user={editing?.user || null}
        onClose={closePanel}
        onSaved={() => { setEditing(null); reread(); }}
      />
    </div>
  );
}

function UserList({ users, error, state, setState, onOpen, onAdd }) {
  const { searchTerm, showInactive } = state;
  const shown = useMemo(() => filterUsers(users || [], { searchTerm, showInactive }), [users, searchTerm, showInactive]);
  return (
    <>
      <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={onAdd}>Add a user</button>
      <CardList
        items={shown}
        getKey={(u) => u.user_id}
        onItemClick={(u) => onOpen(u.user_id)}
        renderCard={(u) => (
          <div data-testid={`user-card-${u.user_id}`}>
            <div className="jq-qcard-top">
              <span className="jq-qcard-title mb-0">{u.name}</span>
              <span className="jq-tag">{u.role}</span>
              {!u.is_active && <span className="jq-tag jq-tag-block">Inactive</span>}
            </div>
            <div className="jq-qcard-meta">#{u.user_id} · {money(u.hourly_wage)}/hr</div>
          </div>
        )}
        loading={users === null}
        error={error}
        empty="No users found."
        testId="user-list"
        search={{ value: searchTerm, onChange: (v) => setState((p) => ({ ...p, searchTerm: v })), placeholder: "Name" }}
        filters={{
          activeCount: showInactive ? 1 : 0,
          resultLabel: `${shown.length} user${shown.length === 1 ? "" : "s"}`,
          onClear: () => setState((p) => ({ ...p, showInactive: false })),
          render: () => (
            <label className="form-check form-switch">
              <input className="form-check-input" type="checkbox" checked={showInactive} onChange={(e) => setState((p) => ({ ...p, showInactive: e.target.checked }))} />
              <span className="form-check-label">Show inactive users</span>
            </label>
          ),
        }}
      />
    </>
  );
}

function UserDetail({ user, loading, error, busy, onAll, onEdit, onDeactivate }) {
  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All users</button>;
  if (loading) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  if (!user) return <>{back}<div className="jq-note jq-note-danger" role="alert">{error || "That user isn't in JobQ."}</div></>;
  return (
    <>
      {back}
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      <section className="jq-card" data-testid="user-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{user.name}</span>
          {!user.is_active && <span className="jq-tag jq-tag-block">Inactive</span>}
        </div>
        <dl className="jq-kv mt-2 mb-0">
          <div><dt>Role</dt><dd>{user.role}</dd></div>
          <div><dt>Hourly wage</dt><dd>{money(user.hourly_wage)}</dd></div>
          <div><dt>User #</dt><dd>{user.user_id}</dd></div>
          <div><dt>Status</dt><dd>{user.is_active ? "Active" : "Inactive"}</dd></div>
        </dl>
      </section>
      <button type="button" className="btn btn-outline-primary w-100 mb-2" disabled={busy} onClick={onEdit}>Edit this user</button>
      {user.is_active && <button type="button" className="btn btn-outline-warning w-100" disabled={busy} onClick={onDeactivate}>Mark inactive</button>}
      {String(user.role || "").toLowerCase() === "admin" && <RememberedPhones user={user} />}
    </>
  );
}

// The phones that remember this Admin's unlock for 30 days, with Sign out (features/users/adminDevices.js,
// the desktop page's own reads).
function RememberedPhones({ user }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const shown = useRef(user.user_id);
  shown.current = user.user_id;

  const load = useCallback(async () => {
    const r = await loadAdminDevices();
    if (shown.current !== user.user_id) return; // another user was opened meanwhile
    setUnavailable(r.unavailable);
    if (r.ok) {
      setRows(devicesForUser(r.rows, user.user_id).filter((d) => d.live));
      setError(null);
    } else {
      setRows([]);
      setError(r.needsPin ? "Unlock admin to see remembered phones." : `Couldn't read the remembered phones: ${r.message}`);
    }
  }, [user.user_id]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const signOut = async (d) => {
    const ok = await phoneConfirm(`${user.name}'s admin unlock is remembered on "${d.label}". Sign that phone out? It will ask for the Admin PIN next time.`, {
      title: "Sign out phone", confirmLabel: "Sign out", danger: true,
    });
    if (!ok) return;
    setBusyId(d.device_id);
    const r = await signOutAdminDevice(d.device_id);
    setBusyId(null);
    if (!r.ok) setError(`Couldn't sign that phone out: ${r.message}`);
    load();
  };

  if (unavailable) return null; // this JobQ doesn't remember phones (yet)
  return (
    <section className="jq-card mt-3" data-testid="remembered-phones">
      <h2 className="jq-section-title mt-0">Remembered phones</h2>
      <div className="jq-muted small mb-2">A lost phone: sign it out here and change the PIN.</div>
      {error && <div className="jq-note jq-note-warn mb-2" role="status">{error}</div>}
      {rows === null && <div className="jq-muted">Loading…</div>}
      {rows && rows.length === 0 && !error && <div className="jq-muted">No phone remembers this admin's unlock.</div>}
      {(rows || []).map((d) => (
        <div key={d.device_id} className="jq-task-line" data-testid={`remembered-phone-${d.device_id}`}>
          <div className="jq-task-line-main">
            <div className="fw-semibold">{d.label}</div>
            <div className="jq-muted">{deviceStatusText(d)}</div>
          </div>
          <button type="button" className="btn btn-outline-danger" disabled={busyId === d.device_id} onClick={() => signOut(d)}>
            {busyId === d.device_id ? "…" : "Sign out"}
          </button>
        </div>
      ))}
    </section>
  );
}

function UserPanel({ open, user, onClose, onSaved }) {
  const [form, setForm] = useState(userForm(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Filled when it opens; the PIN boxes are emptied when it closes too (a PIN never waits in state).
  useEffect(() => {
    if (open) { setForm(userForm(user)); setError(null); } else setForm((f) => ({ ...f, pin: "", confirm_pin: "" }));
  }, [open, user]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setError(null);
    const r = await saveUser({ userId: user ? user.user_id : null, form, ui: phoneUi, setBusy });
    if (r.error) setError(r.error);
    if (r.done) onSaved();
  };
  return (
    <FullScreenPanel
      open={open}
      title={user ? `Edit ${user.name}` : "Add a user"}
      onClose={busy ? undefined : onClose}
      testId="user-panel"
      footer={<button type="button" className="btn btn-primary btn-lg w-100" disabled={busy} onClick={save}>{busy ? "Saving…" : user ? "Save changes" : "Add the user"}</button>}
    >
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      <div className="mb-3">
        <label className="form-label" htmlFor="us-name">Name</label>
        <input id="us-name" className="form-control" value={form.name} onChange={set("name")} autoComplete="off" />
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="us-role">Role</label>
        <select id="us-role" className="form-select" value={form.role} onChange={set("role")}>
          {USER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="us-wage">Hourly wage ($/hr)</label>
        <input id="us-wage" type="number" inputMode="decimal" step="0.01" min="0" className="form-control" value={form.hourly_wage} onChange={set("hourly_wage")} />
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="us-pin">{user ? "New PIN (optional)" : "PIN (4-6 digits)"}</label>
        <input id="us-pin" type="password" inputMode="numeric" maxLength={6} className="form-control" value={form.pin} onChange={set("pin")} autoComplete="one-time-code" data-lpignore="true" data-1p-ignore="true" />
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="us-pin2">Confirm PIN</label>
        <input id="us-pin2" type="password" inputMode="numeric" maxLength={6} className="form-control" value={form.confirm_pin} onChange={set("confirm_pin")} autoComplete="one-time-code" data-lpignore="true" data-1p-ignore="true" />
      </div>
      <label className="form-check">
        <input className="form-check-input" type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
        <span className="form-check-label">Active</span>
      </label>
    </FullScreenPanel>
  );
}
